import { differenceInYears } from 'date-fns';
import _ from 'lodash';
import { getBirthDateFromCpr } from 'utility/date';
import { IApiResponse } from 'utility/node/api-response';
import { ValidationResult } from 'utility/node/validation-result';
import { SentryGroup, addMessageWithGroup } from 'utility/sentry';
import * as yup from 'yup';
import { normalizeBankAccount } from './bankAccount';

yup.addMethod<yup.StringSchema>(
  yup.string,
  'validDawaAddress',
  function (message) {
    return this.test(
      'dawa-test',
      'Angiv venligst en gyldig adresse',
      async function (value) {
        const { path, createError } = this;
        if (!value) {
          return true;
        }

        let valid = false;
        try {
          const response = await fetch(
            `/api/validation/addressById?id=${encodeURIComponent(value)}`,
          );
          valid = response.ok;
        } catch (e) {
          valid = false;
        }

        return valid
          ? true
          : createError({
              message: message || 'Address is not a valid address',
              path: path,
            });
      },
    );
  },
);

yup.addMethod<yup.NumberSchema>(
  yup.number,
  'equalField',
  function (message, fieldValue) {
    return this.test('equalField', 'Matcher ikke felt', async function (value) {
      const { path, createError } = this;
      if (!value) {
        return true;
      }

      return Number(fieldValue) === value
        ? true
        : createError({
            message: message || 'Forkert værdi',
            path: path,
          });
    });
  },
);

yup.addMethod<yup.NumberSchema>(
  yup.number,
  'lessThanOrEqualField',
  function (message, fieldValue) {
    return this.test(
      'lessThanOrEqualField',
      'Forkert værdi',
      async function (value) {
        const { path, createError } = this;
        if (!value) {
          return true;
        }

        if (Number.isNaN(Number(value))) {
          return createError({
            message: message || 'Skal være et tal',
            path: path,
          });
        }

        return Number(value) <= Number(fieldValue)
          ? true
          : createError({
              message: message || 'Forkert værdi',
              path: path,
            });
      },
    );
  },
);

yup.addMethod<yup.NumberSchema>(
  yup.number,
  'greaterThanOrEqualField',
  function (message, fieldValue) {
    return this.test(
      'greaterThanOrEqualField',
      'Forkert værdi',
      async function (value) {
        const { path, createError } = this;
        if (!value) {
          return true;
        }

        if (Number.isNaN(Number(value))) {
          return createError({
            message: message || 'Skal være et tal',
            path: path,
          });
        }

        return Number(value) >= Number(fieldValue)
          ? true
          : createError({
              message: message || 'Forkert værdi',
              path: path,
            });
      },
    );
  },
);
yup.addMethod<yup.StringSchema>(
  yup.string,
  'equalField',
  function (message, fieldValue) {
    return this.test('equalField', 'Matcher ikke felt', async function (value) {
      const { path, createError } = this;
      if (!value) {
        return true;
      }
      return fieldValue === value
        ? true
        : createError({
            message: message || 'Forkert værdi',
            path: path,
          });
    });
  },
);

yup.addMethod<yup.StringSchema>(
  yup.string,
  'lessThanOrEqualField',
  function (message, fieldValue) {
    return this.test(
      'lessThanOrEqualField',
      'Forkert værdi',
      async function (value) {
        const { path, createError } = this;
        if (!value) {
          return true;
        }

        if (Number.isNaN(Number(value))) {
          return createError({
            message: message || 'Skal være et tal',
            path: path,
          });
        }

        return Number(value) <= Number(fieldValue)
          ? true
          : createError({
              message: message || 'Forkert værdi',
              path: path,
            });
      },
    );
  },
);

yup.addMethod<yup.StringSchema>(
  yup.string,
  'greaterThanOrEqualField',
  function (message, fieldValue) {
    return this.test(
      'greaterThanOrEqualField',
      'Forkert værdi',
      async function (value) {
        const { path, createError } = this;
        if (!value) {
          return true;
        }

        if (Number.isNaN(Number(value))) {
          return createError({
            message: message || 'Skal være et tal',
            path: path,
          });
        }

        return Number(value) >= Number(fieldValue)
          ? true
          : createError({
              message: message || 'Forkert værdi',
              path: path,
            });
      },
    );
  },
);

const getAgeFromCpr = (value: string) => {
  const birthDate = getBirthDateFromCpr(value);
  const currentDate = new Date();
  return differenceInYears(currentDate, birthDate);
};

yup.addMethod<yup.StringSchema>(
  yup.string,
  'minAgeFromCpr',
  function (message, minAge: number) {
    return this.test('minAgeFromCpr', 'Ugyldig alder', async function (value) {
      const { path, createError } = this;
      if (!value) {
        return true;
      }
      if (value.length < 11) {
        return true;
      }
      const age = getAgeFromCpr(value);
      return (
        age >= minAge ||
        createError({
          message: message,
        })
      );
    });
  },
);
yup.addMethod<yup.StringSchema>(
  yup.string,
  'maxAgeFromCpr',
  function (message, maxAge: number) {
    return this.test('maxAgeFromCpr', 'Ugyldig alder', async function (value) {
      const { path, createError } = this;
      if (!value) {
        return true;
      }
      if (value.length < 11) {
        return true;
      }
      const age = getAgeFromCpr(value);
      return (
        age <= maxAge ||
        createError({
          message: message,
        })
      );
    });
  },
);

const getBankRegistrationCodeValidations = async (value: string) => {
  const validationResponse = await fetch(
    `/api/validation/bankRegistrationCode?regCode=${value}`,
  );
  if (!validationResponse.ok) {
    const message = `API request failed with status code: ${validationResponse.status}`;
    addMessageWithGroup({
      message: message,
      group: SentryGroup.validation,
      tag: 'getBankRegistrationCodeValidations',
      printToConsole: true,
      consoleMessage: message,
    });
  }
  const jsonResponse =
    (await validationResponse.json()) as IApiResponse<ValidationResult>;

  return jsonResponse.content?.valid;
};

const debouncedBankRegistrationCodeValidations = _.debounce(
  getBankRegistrationCodeValidations,
  300,
  {
    trailing: true,
  },
);

yup.addMethod<yup.StringSchema>(
  yup.string,
  'validBankRegNo',
  function (message) {
    return this.test(
      'bank-reg-no-test',
      'Angiv venligst et gyldigt bank registreringsnummer',
      function (value) {
        return new Promise(async (resolve, reject) => {
          const { path, createError } = this;
          if (!value) {
            return resolve(true);
          }

          if (value.length < 4) return resolve(false);

          const validation = await debouncedBankRegistrationCodeValidations(
            value.replace(/-.*/, ''),
          );
          resolve(
            validation ||
              createError({
                message: message || 'Bank registration code is not valid',
                path: path,
              }),
          );
        });
      },
    );
  },
);

const getBankAccountNoValidations = async (value: string) => {
  if (!value || value.indexOf('-') === -1) {
    return false;
  }
  const [regCode, accountNo] = normalizeBankAccount(value).split('-');
  if (!/^\d{4}$/.test(regCode) || !/^\d{10}$/.test(accountNo)) {
    return false;
  }
  const validationResponse = await fetch(
    `/api/validation/bankAccountNo?regCode=${regCode}&accountNo=${accountNo}`,
  );
  if (!validationResponse.ok) {
    const message = `API request failed with status code: ${validationResponse.status}`;
    addMessageWithGroup({
      message: message,
      group: SentryGroup.validation,
      tag: 'getBankAccountNoValidations',
      printToConsole: true,
      consoleMessage: message,
    });
  }
  const jsonResponse =
    (await validationResponse.json()) as IApiResponse<ValidationResult>;

  return jsonResponse.content?.valid;
};

const debouncedGetBankAccountNoValidations = _.debounce(
  getBankAccountNoValidations,
  300,
  {
    trailing: false,
    leading: true,
  },
);

yup.addMethod<yup.StringSchema>(
  yup.string,
  'validBankAccountNo',
  function (message) {
    return this.test(
      'bank-reg-no-test',
      'Angiv venligst et gyldigt konto-nummer',
      function (value) {
        return new Promise(async (resolve, reject) => {
          const { path, createError } = this;
          if (!value) {
            return resolve(true);
          }

          const validation = await debouncedGetBankAccountNoValidations(value);
          resolve(
            validation ||
              createError({
                message: message || 'Bank account no is not valid',
                path: path,
              }),
          );
        });
      },
    );
  },
);

yup.addMethod<yup.DateSchema>(
  yup.date,
  'dynamicField',
  function (message, fieldValue, validationValue) {
    return this.test(
      'dynamicField',
      'invalid dynamicField value',
      async function (value) {
        return dynamicValidation({
          context: this,
          message,
          fieldValue,
          validationValue,
          value,
        });
      },
    );
  },
);

yup.addMethod<yup.StringSchema>(
  yup.string,
  'dynamicField',
  function (message, fieldValue, validationValue) {
    return this.test(
      'dynamicField',
      'invalid dynamicField value',
      async function (value) {
        return dynamicValidation({
          context: this,
          message,
          fieldValue,
          validationValue,
          value,
        });
      },
    );
  },
);

const dynamicValidation = ({
  context,
  message,
  fieldValue,
  validationValue,
  value,
}) => {
  const { createError } = context;
  if (!fieldValue) {
    return true;
  }
  if (value && fieldValue === validationValue) {
    return true;
  }

  return createError({ message });
};

export default yup;
